import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { COMUNE_COORDS } from "../../utils/comuniCoords";

// Silver dot pin matching LEOVERA's palette — avoids Leaflet's default marker
// image bug under Webpack (broken relative asset paths) entirely.
const pinIcon = L.divIcon({
  className: "leovera-map-pin",
  html: '<span style="display:block;width:16px;height:16px;border-radius:50%;background:#C0C0C0;border:2px solid #060A14;box-shadow:0 0 0 4px rgba(192,192,192,0.25)"></span>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  popupAnchor: [0, -10],
});

// Fits the map's initial view to every pin (instead of a fixed center/zoom)
// so no comune is ever off-screen when the dialog opens. Extra top padding
// leaves room for the permanent name tooltip above the topmost pin.
const FitBounds = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 1) {
      map.fitBounds(positions, { paddingTopLeft: [36, 70], paddingBottomRight: [36, 36], maxZoom: 11 });
    } else if (positions.length === 1) {
      map.setView(positions[0], 12);
    }
  }, [map, positions]);
  return null;
};

// Interactive map of every free-shipping comune. Only comuni with known
// coordinates (comuniCoords.js) get a pin — others still ship free, they're
// just not plotted yet. Clicking a pin (or its "Seleziona" button) calls
// onSelect({ name, cap }) so the checkout form can autofill CAP + provincia.
// Name tooltips are hover-only (not permanent): with an admin-configurable,
// unbounded list of comuni there's no safe static layout that avoids label
// overlap, so each pin shows its name on hover/tap and in the click popup.
export const FreeShippingMap = ({ comuni = [], capFor, onSelect }) => {
  const pins = useMemo(
    () => comuni
      .map((name) => ({ name, coords: COMUNE_COORDS[name], cap: capFor ? capFor(name) : "" }))
      .filter((p) => p.coords),
    [comuni, capFor]
  );
  const positions = useMemo(() => pins.map((p) => p.coords), [pins]);

  const center = pins.length ? pins[Math.floor(pins.length / 2)].coords : [37.4, 15.0];

  return (
    <div className="rounded-sm overflow-hidden border border-white/10" data-testid="free-shipping-map">
      <MapContainer center={center} zoom={9} scrollWheelZoom={false} style={{ height: "360px", width: "100%" }}>
        <FitBounds positions={positions} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {pins.map((p) => (
          <Marker key={p.name} position={p.coords} icon={pinIcon}>
            <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>{p.name}</span>
            </Tooltip>
            <Popup>
              <div style={{ fontSize: 13, lineHeight: 1.5 }} data-testid={`free-shipping-map-popup-${p.name}`}>
                <b>{p.name}</b>{p.cap ? ` · CAP ${p.cap}` : ""}
                <br />
                <span style={{ color: "#059669" }}>Spedizione gratuita</span>
                {onSelect && (
                  <>
                    <br />
                    <button
                      type="button"
                      onClick={() => onSelect(p)}
                      data-testid={`free-shipping-map-select-${p.name}`}
                      style={{
                        marginTop: 6, background: "#C0C0C0", color: "#060A14", border: "none",
                        borderRadius: 3, padding: "5px 12px", fontSize: 11, cursor: "pointer",
                        textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600,
                      }}
                    >
                      Seleziona
                    </button>
                  </>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};
